import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StatusChip from '../atoms/StatusChip';
import TablePaginationActions from '../molecules/TablePaginationActions';
import { REQUEST_ROWS_PER_PAGE_OPTIONS } from '../../constants';
import { formatDisplayedRows } from '../../utils/format';
import type { RequestRow } from '../../interfaces/Dashboard';

const COLUMN_COUNT = 7;

interface RequestsTableProps {
  rows: RequestRow[];
  totalCount: number | null;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onViewJobSummary: (row: RequestRow) => void;
}

/**
 * Request grid for the selected health system. Server-paginated — `rows` is
 * already the current page, and `totalCount` comes from the API.
 */
const RequestsTable = ({
  rows,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  onPageChange,
  onRowsPerPageChange,
  onViewJobSummary,
}: RequestsTableProps) => (
  <TableContainer className='request-table-list'>
    <Table aria-label='retrieval requests' size='small'>
      <TableHead className='request-table-list-header'>
        <TableRow>
          <TableCell align='center'>ROI Request ID</TableCell>
          <TableCell align='center'>Vendor Request ID</TableCell>
          <TableCell align='center'>Request ID</TableCell>
          <TableCell align='center'>Updated Date</TableCell>
          <TableCell align='center'>Purpose of Use</TableCell>
          <TableCell align='center'>File Status</TableCell>
          <TableCell align='center'>Action</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.id}
            id={String(row.id)}
            className='request-table-row'
          >
            <TableCell
              align='center'
              className='request-table-column name capital-text'
            >
              {row.requesterrefid}
            </TableCell>
            <TableCell
              align='center'
              className='request-table-column name capital-text'
            >
              {row.vendorrequestid}
            </TableCell>
            <TableCell
              align='center'
              className='request-table-column name capital-text'
            >
              {row.requestid}
            </TableCell>
            <TableCell
              align='center'
              className='request-table-column name capital-text'
            >
              {row.updatedat}
            </TableCell>
            <TableCell
              align='center'
              className='request-table-column name capital-text'
            >
              {row.purposeofuse}
            </TableCell>
            <TableCell
              align='center'
              className='request-table-column name capital-text'
            >
              <StatusChip fileStatus={row.filestatus} />
            </TableCell>
            <TableCell
              align='center'
              className='request-table-column name capital-text'
            >
              <div
                className='action-btn-wrapper'
                style={{ justifyContent: 'center' }}
              >
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={() => onViewJobSummary(row)}
                  disabled={isLoading || Boolean(row.showJobSummaryLoader)}
                  title='Job Summary'
                  aria-label={'Job summary for request ' + row.requesterrefid}
                >
                  <VisibilityIcon />
                  {row.showJobSummaryLoader ? (
                    <Box className='CustLodder' padding={1}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : null}
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}

        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} align='center'>
              {isLoading ? (
                <Box padding={1} sx={{ textAlign: 'center' }}>
                  <CircularProgress size={40} />
                </Box>
              ) : (
                'No Record Found'
              )}
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>

      {rows.length > 0 ? (
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={REQUEST_ROWS_PER_PAGE_OPTIONS}
              colSpan={COLUMN_COUNT}
              count={totalCount ?? 0}
              rowsPerPage={rowsPerPage}
              page={page}
              labelDisplayedRows={formatDisplayedRows}
              slotProps={{
                select: {
                  inputProps: { 'aria-label': 'Records per page' },
                  native: true,
                },
              }}
              onPageChange={(_event, newPage) => onPageChange(newPage)}
              onRowsPerPageChange={(event) =>
                onRowsPerPageChange(parseInt(event.target.value, 10))
              }
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      ) : null}
    </Table>
  </TableContainer>
);

export default RequestsTable;
